/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { chatAPI, ttsAPI, sessionAPI, settingsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Send, Volume2, VolumeX, Menu, Plus, Trash2, 
  FileText, Home, BarChart3, Leaf, Settings, LogOut, 
  AlertTriangle, Phone, Heart 
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '../components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const AIAvatar = ({ isSpeaking }) => (
  <div className="relative">
    {/* Breathing ring */}
    <div className={`absolute inset-0 rounded-full bg-primary/20 ${isSpeaking ? 'animate-pulse-ring' : 'animate-breathe'}`} />
    {/* Avatar container */}
    <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-sage-300 to-sage-500 flex items-center justify-center shadow-lg overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-t from-sage-600/30 to-transparent" />
      {/* Simple friendly face */}
      <svg viewBox="0 0 100 100" className="w-16 h-16">
        {/* Eyes */}
        <ellipse cx="35" cy="42" rx="6" ry="8" fill="#2D3A3A" className={isSpeaking ? 'animate-pulse' : ''} />
        <ellipse cx="65" cy="42" rx="6" ry="8" fill="#2D3A3A" className={isSpeaking ? 'animate-pulse' : ''} />
        {/* Eye shine */}
        <circle cx="33" cy="39" r="2" fill="white" opacity="0.8" />
        <circle cx="63" cy="39" r="2" fill="white" opacity="0.8" />
        {/* Gentle smile */}
        <path d="M 35 60 Q 50 75 65 60" stroke="#2D3A3A" strokeWidth="3" fill="none" strokeLinecap="round" />
      </svg>
    </div>
    {/* Online indicator */}
    <div className="absolute bottom-1 right-1 w-4 h-4 rounded-full bg-emerald-400 border-2 border-white" />
  </div>
);

const TypingIndicator = () => (
  <div className="flex items-center gap-1 px-4 py-3">
    <div className="typing-dot w-2 h-2 rounded-full bg-muted-foreground/50" />
    <div className="typing-dot w-2 h-2 rounded-full bg-muted-foreground/50" />
    <div className="typing-dot w-2 h-2 rounded-full bg-muted-foreground/50" />
  </div>
);

const CrisisModal = ({ isOpen, onClose }) => (
  <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <div className="mx-auto w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mb-4">
          <Heart className="w-8 h-8 text-accent" />
        </div>
        <DialogTitle className="text-center text-xl">You're Not Alone</DialogTitle>
        <DialogDescription className="text-center">
          It sounds like you're going through something really difficult. 
          Please reach out to someone who can help.
        </DialogDescription>
      </DialogHeader>
      
      <div className="space-y-4 py-4">
        <div className="p-4 rounded-2xl bg-accent/10 border border-accent/20">
          <h4 className="font-semibold flex items-center gap-2 text-foreground mb-3">
            <Phone className="w-4 h-4 text-accent" />
            Crisis Resources
          </h4>
          <ul className="space-y-2 text-sm">
            <li><strong>988 Suicide & Crisis Lifeline:</strong> Call or text 988</li>
            <li><strong>Crisis Text Line:</strong> Text HOME to 741741</li>
            <li><strong>International:</strong> findahelpline.com</li>
          </ul>
        </div>
        
        <p className="text-sm text-muted-foreground text-center">
          You deserve real support. Please reach out to these resources or someone you trust.
        </p>
      </div>
      
      <Button onClick={onClose} className="w-full rounded-full" data-testid="crisis-modal-close">
        I Understand
      </Button>
    </DialogContent>
  </Dialog>
);

const Chat = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showCrisis, setShowCrisis] = useState(false);
  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    loadSettings();
    loadSessions();
    startNewSession();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setVoiceEnabled(response.data.voice_enabled ?? true);
    } catch (error) {
      console.error('Settings error:', error);
    }
  };

  const loadSessions = async () => {
    try {
      const response = await chatAPI.getSessions();
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Sessions error:', error);
    }
  };

  const startNewSession = () => {
    const newSessionId = `session_${Date.now()}`;
    setSessionId(newSessionId);
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: `Hello${user?.name ? `, ${user.name}` : ''}! I'm your TPOCARE companion. I'm here to listen and support you. How are you feeling today? Remember, this is a safe space - you can share whatever is on your mind.`,
      timestamp: new Date().toISOString()
    }]);
  };

  const loadSession = async (sid) => {
    try {
      const response = await chatAPI.getHistory(sid);
      setSessionId(sid);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Load session error:', error);
    }
  };

  const deleteSession = async (sid) => {
    try {
      await chatAPI.deleteSession(sid);
      loadSessions();
      if (sid === sessionId) {
        startNewSession();
      }
    } catch (error) {
      console.error('Delete session error:', error);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: input,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await chatAPI.sendMessage(input, sessionId);
      const aiMessage = {
        id: response.data.id,
        role: 'assistant',
        content: response.data.content,
        is_crisis: response.data.is_crisis,
        timestamp: response.data.timestamp
      };

      setMessages(prev => [...prev, aiMessage]);

      if (response.data.is_crisis) {
        setShowCrisis(true);
      }

      if (voiceEnabled && !response.data.is_crisis) {
        playVoice(response.data.content);
      }

      loadSessions();
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        id: `error_${Date.now()}`,
        role: 'assistant',
        content: "I'm sorry, I'm having trouble responding right now. Please try again.",
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const playVoice = async (text) => {
    try {
      setIsSpeaking(true);
      const audioUrl = await ttsAPI.generateSpeech(text.substring(0, 500));
      
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.play();
        audioRef.current.onended = () => setIsSpeaking(false);
      }
    } catch (error) {
      console.error('TTS error:', error);
      setIsSpeaking(false);
    }
  };

  const generateSummary = async () => {
    try {
      await sessionAPI.generateSummary(sessionId);
      navigate('/dashboard');
    } catch (error) {
      console.error('Summary error:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="h-screen flex bg-background">
      {/* Sidebar */}
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="fixed top-4 left-4 z-50 lg:hidden" data-testid="menu-btn">
            <Menu className="w-5 h-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-72 p-0">
          <Sidebar 
            sessions={sessions} 
            currentSession={sessionId}
            onNewSession={startNewSession}
            onLoadSession={loadSession}
            onDeleteSession={deleteSession}
            onNavigate={navigate}
            onLogout={logout}
            user={user}
          />
        </SheetContent>
      </Sheet>

      {/* Desktop Sidebar */}
      <div className="hidden lg:block w-72 border-r border-border">
        <Sidebar 
          sessions={sessions} 
          currentSession={sessionId}
          onNewSession={startNewSession}
          onLoadSession={loadSession}
          onDeleteSession={deleteSession}
          onNavigate={navigate}
          onLogout={logout}
          user={user}
        />
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="h-14 sm:h-16 border-b border-border flex items-center justify-between px-3 sm:px-6 bg-card/50 backdrop-blur-xl">
          <div className="flex items-center gap-2 sm:gap-3 lg:pl-0 pl-10">
            <div className="hidden sm:block">
              <AIAvatar isSpeaking={isSpeaking} />
            </div>
            <div className="sm:hidden w-10 h-10 rounded-full bg-gradient-to-br from-sage-300 to-sage-500 flex items-center justify-center">
              <svg viewBox="0 0 100 100" className="w-6 h-6">
                <ellipse cx="35" cy="42" rx="5" ry="6" fill="#2D3A3A" />
                <ellipse cx="65" cy="42" rx="5" ry="6" fill="#2D3A3A" />
                <path d="M 35 60 Q 50 72 65 60" stroke="#2D3A3A" strokeWidth="3" fill="none" strokeLinecap="round" />
              </svg>
            </div>
            <div>
              <h2 className="font-semibold text-foreground text-sm sm:text-base">TPOCARE AI</h2>
              <p className="text-xs text-muted-foreground hidden sm:block">Your wellness companion</p>
            </div>
          </div>
          
          <div className="flex items-center gap-1 sm:gap-2">
            <div className="relative group">
              <Button
                variant="outline"
                size="sm"
                onClick={() => user?.name !== "Guest User" && navigate('/voice-call')}
                className={`rounded-full px-2 sm:px-3 h-8 sm:h-9 ${user?.name === "Guest User" ? 'bg-gray-200 text-gray-400 border-gray-300 cursor-not-allowed' : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/20'}`}
                disabled={user?.name === "Guest User"}
                data-testid="voice-call-btn"
              >
                <Phone className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Voice Call</span>
              </Button>
              {user?.name === "Guest User" && (
                <span className="absolute left-1/2 -top-6 -translate-x-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                  You should login to try this feature
                </span>
              )}
            </div>

            
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setVoiceEnabled(!voiceEnabled)}
              className="rounded-full h-8 w-8 sm:h-9 sm:w-9"
              data-testid="voice-toggle"
            >
              {voiceEnabled ? <Volume2 className="w-4 h-4 sm:w-5 sm:h-5" /> : <VolumeX className="w-4 h-4 sm:w-5 sm:h-5" />}
            </Button>
            
            {messages.length > 2 && (
              <Button
                variant="outline"
                size="sm"
                onClick={generateSummary}
                className="rounded-full hidden md:flex"
                data-testid="summary-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Summary
              </Button>
            )}
          </div>
        </header>

        {/* Messages */}
        <ScrollArea className="flex-1 p-3 sm:p-6">
          <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6">
            {messages.map((message, index) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div
                  className={`max-w-[90%] sm:max-w-[85%] p-3 sm:p-4 ${
                    message.role === 'user'
                      ? 'message-user'
                      : message.is_crisis
                      ? 'message-ai crisis-mode'
                      : 'message-ai'
                  }`}
                  data-testid={`message-${message.role}`}
                >
                  {message.is_crisis && (
                    <div className="flex items-center gap-2 text-accent mb-2 font-medium text-sm">
                      <AlertTriangle className="w-4 h-4" />
                      Support Resources
                    </div>
                  )}
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
                  <p className="text-xs opacity-60 mt-2">
                    {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="message-ai">
                  <TypingIndicator />
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Input Area */}
        <div className="p-3 sm:p-6 border-t border-border bg-card/50 backdrop-blur-xl">
          <div className="max-w-2xl mx-auto">
            <div className="flex gap-2 sm:gap-3 items-center glass rounded-2xl p-1.5 sm:p-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Share what's on your mind..."
                className="flex-1 border-0 bg-transparent focus-visible:ring-0 text-sm sm:text-base h-9 sm:h-10"
                disabled={isLoading}
                data-testid="chat-input"
              />
              <Button
                onClick={sendMessage}
                disabled={!input.trim() || isLoading}
                className="rounded-full h-8 w-8 sm:h-10 sm:w-10 p-0"
                data-testid="send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-center text-muted-foreground mt-2 sm:mt-3 px-2">
              TPOCARE AI provides emotional support, not medical advice. In crisis situations, contact emergency services.
            </p>
          </div>
        </div>
      </div>

      {/* Crisis Modal */}
      <CrisisModal isOpen={showCrisis} onClose={() => setShowCrisis(false)} />
      
      {/* Hidden audio element */}
      <audio ref={audioRef} className="hidden" />
    </div>
  );
};

const Sidebar = ({ sessions, currentSession, onNewSession, onLoadSession, onDeleteSession, onNavigate, onLogout, user, isSheet = false }) => (
  <div className="h-full flex flex-col bg-card">
    <div className="p-6 border-b border-border">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Leaf className="w-5 h-5 text-primary" />
        </div>
        <div>
          <div className="font-heading font-bold text-foreground">TPOCARE</div>
          <div className="text-xs text-muted-foreground">{user?.name || 'Guest'}</div>
        </div>
      </div>
    </div>

    <div className="flex-1 p-4 space-y-2 overflow-auto">
      <Button
        onClick={onNewSession}
        className="w-full justify-start rounded-xl"
        data-testid="new-chat-btn"
      >
        <Plus className="w-4 h-4 mr-2" />
        New Conversation
      </Button>

      <div className="text-xs font-medium text-muted-foreground px-3 py-2">Recent</div>
      
      {sessions.slice(0, 10).map((session) => (
        <div
          key={session.session_id}
          className={`group flex items-center gap-2 p-3 rounded-xl cursor-pointer transition-colors ${
            session.session_id === currentSession ? 'bg-primary/10' : 'hover:bg-muted/50'
          }`}
          onClick={() => onLoadSession(session.session_id)}
        >
          <div className="flex-1 truncate text-sm">{session.last_message}</div>
          <Button
            variant="ghost"
            size="icon"
            className="w-8 h-8 opacity-0 group-hover:opacity-100"
            onClick={(e) => { e.stopPropagation(); onDeleteSession(session.session_id); }}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      ))}
    </div>

    <div className="p-4 border-t border-border space-y-1">
      <div className="relative group">
        <Button
          variant="ghost"
          className={`w-full justify-start rounded-xl ${user?.name === "Guest User" ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20'}`}
          onClick={() => user?.name !== "Guest User" && onNavigate('/voice-call')}
          disabled={user?.name === "Guest User"}
          data-testid="nav-voice-call"
        >
          <Phone className="w-4 h-4 mr-3" />
          Voice Call
        </Button>
        {user?.name === "Guest User" && (
          <span className="absolute left-1/2 -top-6 -translate-x-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
            You should login to try this feature
          </span>
        )}
      </div>


      <Button variant="ghost" className="w-full justify-start rounded-xl" onClick={() => onNavigate('/dashboard')} data-testid="nav-dashboard">
        <BarChart3 className="w-4 h-4 mr-3" />
        Mood Dashboard
      </Button>
      <Button variant="ghost" className="w-full justify-start rounded-xl" onClick={() => onNavigate('/exercises')} data-testid="nav-exercises">
        <Leaf className="w-4 h-4 mr-3" />
        Exercises
      </Button>
      <Button variant="ghost" className="w-full justify-start rounded-xl" onClick={() => onNavigate('/settings')} data-testid="nav-settings">
        <Settings className="w-4 h-4 mr-3" />
        Settings
      </Button>
      <Button variant="ghost" className="w-full justify-start rounded-xl text-destructive hover:text-destructive" onClick={onLogout} data-testid="logout-btn">
        <LogOut className="w-4 h-4 mr-3" />
        Logout
      </Button>
    </div>
  </div>
);

export default Chat;
